import api from './api';

/**
 * Unified Lock/Unlock API Service
 * Consolidates all lock/unlock functionality to use the AccrualUnlockRequest system
 */
const lockUnlockApi = {
  // ========================================
  // LOCK OPERATIONS
  // ========================================
  
  /**
   * Lock a specific row
   * @param {number} rowId - The row ID to lock
   * @returns {Promise} API response
   */
  lockRow: (rowId) => 
    api.post(`/accrual-detail/${rowId}/lock/`),

  // ========================================
  // UNLOCK REQUEST OPERATIONS  
  // ========================================
  
  /**
   * Create an unlock request for a specific row
   * @param {number} rowId - The row ID to request unlock for
   * @param {string} reason - Reason for unlock request
   * @returns {Promise} API response
   */
  createUnlockRequest: (rowId, reason) =>
    api.post(`/accrual-detail/${rowId}/unlock-request/`, { reason }),

  /**
   * Create an unlock request with file attachment
   * @param {number} rowId - The row ID to request unlock for
   * @param {string} reason - Reason for unlock request
   * @param {File} attachment - PDF file attachment (optional)
   * @returns {Promise} API response
   */
  createUnlockRequestWithAttachment: (rowId, reason, attachment) => {
    const formData = new FormData();
    formData.append('reason', reason);
    
    if (attachment) {
      formData.append('attachment', attachment);
    }
    
    return api.post(`/accrual-detail/${rowId}/unlock-request/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  /**
   * Create bulk unlock requests for multiple rows
   * @param {number[]} rowIds - Array of row IDs
   * @param {string} note - Note for bulk request
   * @returns {Promise} API response
   */
  createBulkUnlockRequest: (rowIds, note) =>
    api.post('/accrual-detail/bulk-unlock-request/', { row_ids: rowIds, note }),

  // ========================================
  // UNLOCK REQUEST MANAGEMENT (Admin)
  // ========================================
  
  /**
   * Get all unlock requests with optional filters
   * @param {Object} params - Query parameters (page, page_size, status, campaign_id, etc.)
   * @returns {Promise} API response
   */
  getUnlockRequests: (params = {}) =>
    api.get('/unlock-requests/', { params }),

  /**
   * Get unlock requests for a specific campaign
   * @param {number} campaignId - Campaign ID
   * @param {string} status - Optional status filter
   * @returns {Promise} API response
   */
  getUnlockRequestsByCampaign: (campaignId, status = null) => {
    const params = { campaign_id: campaignId };
    if (status) params.status = status;
    return api.get('/unlock-requests/', { params });
  },

  /**
   * Get unlock requests for a specific row
   * @param {number} rowId - Row ID (accrual_row_id)
   * @param {string} status - Optional status filter
   * @returns {Promise} API response
   */
  getUnlockRequestsByRow: (rowId, status = null) => {
    const params = { accrual_row_id: rowId };
    if (status) params.status = status;
    return api.get('/unlock-requests/', { params });
  },

  /**
   * Get details of a specific unlock request
   * @param {number} requestId - Request ID
   * @returns {Promise} API response
   */
  getUnlockRequestDetails: (requestId) =>
    api.get(`/unlock-requests/${requestId}/`),

  // ========================================
  // APPROVAL/REJECTION OPERATIONS (Admin)
  // ========================================
  
  /**
   * Approve an unlock request
   * @param {number} requestId - Request ID
   * @param {string} reviewNote - Optional admin note
   * @returns {Promise} API response
   */
  approveUnlockRequest: (requestId, reviewNote = '') =>
    api.post(`/unlock-request/${requestId}/approve/`, { review_note: reviewNote }),

  /**
   * Reject an unlock request
   * @param {number} requestId - Request ID
   * @param {string} reviewNote - Admin note (required for rejection)
   * @returns {Promise} API response
   */
  rejectUnlockRequest: (requestId, reviewNote) =>
    api.post(`/unlock-request/${requestId}/reject/`, { review_note: reviewNote }),

  /**
   * Bulk approve unlock requests
   * @param {number[]} requestIds - Array of request IDs
   * @param {string} reviewNote - Optional admin note
   * @returns {Promise} API response
   */
  bulkApproveUnlockRequests: (requestIds, reviewNote = '') =>
    api.post('/unlock-request/bulk-approve/', { request_ids: requestIds, review_note: reviewNote }),

  /**
   * Bulk reject unlock requests
   * @param {number[]} requestIds - Array of request IDs
   * @param {string} reviewNote - Admin note (required for rejection)
   * @returns {Promise} API response
   */
  bulkRejectUnlockRequests: (requestIds, reviewNote) =>
    api.post('/unlock-request/bulk-reject/', { request_ids: requestIds, review_note: reviewNote }),

  // ========================================
  // METRICS AND ANALYTICS
  // ========================================
  
  /**
   * Get unlock request metrics
   * @param {Object} filters - Date range and other filters
   * @returns {Promise} API response
   */
  getUnlockRequestMetrics: (filters = {}) =>
    api.get('/unlock-request/metrics/', { params: filters }),

  // ========================================
  // STATUS CHECK OPERATIONS
  // ========================================
  
  /**
   * Check if a row has pending unlock requests
   * @param {number} rowId - Row ID
   * @returns {Promise<boolean>} Whether row has pending requests
   */
  hasPendingUnlockRequests: async (rowId) => {
    try {
      const response = await api.get('/unlock-requests/', { 
        params: { accrual_row_id: rowId, status: 'pending' } 
      });
      return (response.data || []).length > 0;
    } catch (error) {
      console.error('Error checking pending unlock requests:', error);
      return false;
    }
  },

  /**
   * Get lock/unlock status for multiple rows
   * @param {number[]} rowIds - Array of row IDs
   * @returns {Promise<Object>} Status map by row ID
   */
  getRowsUnlockStatus: async (rowIds) => {
    try {
      const response = await api.get('/unlock-requests/', { 
        params: { status: 'pending' } 
      });
      const pendingRequests = response.data || [];
      
      const statusMap = {};
      pendingRequests.forEach(request => {
        const rowId = request.accrual_row || request.row_id;
        if (rowIds.includes(rowId)) {
          if (!statusMap[rowId]) {
            statusMap[rowId] = [];
          }
          statusMap[rowId].push(request);
        }
      });
      
      return statusMap;
    } catch (error) {
      console.error('Error getting rows unlock status:', error);
      return {};
    }
  }
};

export default lockUnlockApi; 